<template>
    <div class="SupportReader">
        <xscp
            ref="referenceXscp"
            @descriptionUpdated="updateStep('xscp', 'description', $event)"
            @componentUpdated="updateStep('xscp', 'component', $event)"
            @propsUpdated="updateStep('xscp', 'props', $event)"
            @stateUpdated="updateStep('xscp', 'state', $event)"
            @scriptLoaded="scriptLoaded"
            @xscpReady="goToHardware"
        />
        <hardware
            ref="referenceHardware"
            @descriptionUpdated="updateStep('hardware', 'description', $event)"
            @componentUpdated="updateStep('hardware', 'component', $event)"
            @stateUpdated="updateStep('hardware', 'state', $event)"
            @hardwareReady="goToSoftware"
        />
        <software
            ref="referenceSoftware"
            :operationType
            :supportId
            @descriptionUpdated="updateStep('software', 'description', $event)"
            @componentUpdated="updateStep('software', 'component', $event)"
            @propsUpdated="updateStep('software', 'props', $event)"
            @stateUpdated="updateStep('software', 'state', $event)"
            @softwareReady="goToCardOperations"
        />
        <card-operations
            ref="referenceCardOperations"
            :operationType
            :supportId
            @descriptionUpdated="updateStep(isReadOnly ? 'software' : 'cardOperations', 'description', $event)"
            @componentUpdated="updateStep(isReadOnly ? 'software' : 'cardOperations', 'component', $event)"
            @operationFinished="operationFinished"
            @propsUpdated="updateStep(isReadOnly ? 'software' : 'cardOperations', 'props', $event)"
            @stateUpdated="updateStep(isReadOnly ? 'software' : 'cardOperations', 'state', $event)"
        />
        <stepper
            v-if="supportReaderScriptLoaded"
            :currentStep
            :steps
            @downloadXSCP="downloadXSCP"
            @goToHardware="goToHardware"
            @goToSoftware="goToSoftware"
            @goToDrivers="goToDrivers"
            @initConnection="initConnection"
            @launchXSCP="launchXSCP"
            @seeCardContent="seeCardContent"
        />
    </div>
</template>

<script setup>
import Stepper from '@/StoreWeb/components/common/stepper/Stepper.vue'
import CardOperations from '@/StoreWeb/components/miscellaneous/conduent-support-reader/card-operations/CardOperations.vue'
import Hardware from '@/StoreWeb/components/miscellaneous/conduent-support-reader/hardware/Hardware'
import Software from '@/StoreWeb/components/miscellaneous/conduent-support-reader/software/Software'
import Xscp from '@/StoreWeb/components/miscellaneous/conduent-support-reader/xscp/Xscp'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const emit = defineEmits(['operationFinished'])

const props = defineProps({
    operationType: {
        type: String,
        default: 'READ',
        validator: (val) => ['READ', 'WRITE'].includes(val)
    },
    supportId: {
        type: String,
        default: ''
    }
})

const { t } = useI18n()

const cardOperationsParams = ref(null)
const currentStep = ref(0)
const supportReaderScriptLoaded = ref(false)

const stepDatas = ref({
    xscp: { description: '', component: null, props: null, state: '' },
    hardware: { description: '', component: null, props: null, state: '' },
    software: { description: '', component: null, props: null, state: '' },
    cardOperations: { description: '', component: null, props: null, state: '' }
})

const referenceXscp = ref(null)
const referenceHardware = ref(null)
const referenceSoftware = ref(null)
const referenceCardOperations = ref(null)

const steps = computed(() => {
    const dynamicSteps = [
        {
            title: t('card_reader:initialization:title'),
            ...stepDatas.value.xscp
        },
        {
            title: t('card_reader:hardware:title'),
            ...stepDatas.value.hardware
        },
        {
            title: t('card_reader:software:title'),
            ...stepDatas.value.software
        }
    ]

    if (props.operationType === 'WRITE') {
        dynamicSteps.push({
            title: t('card_reader:loading:title'),
            ...stepDatas.value.cardOperations
        })
    }

    return dynamicSteps
})

const isReadOnly = computed(() => props.operationType === 'READ')

/* ------------------------------------------------------------- */
/*                        STEPS EVENTS                           */
/* ------------------------------------------------------------- */
function updateStep (step, key, value) {
    stepDatas.value[step][key] = value

    if (key === 'description') {
        stepDatas.value[step].component = null
        stepDatas.value[step].props = null
    }
    if (key === 'component') {
        stepDatas.value[step].description = ''
    }
}

/* ------------------------------------------------------------- */
/*                         XSCP EVENTS                           */
/* ------------------------------------------------------------- */
function goToHardware () {
    currentStep.value = 1
    referenceHardware.value.detectReader()
}

function scriptLoaded () {
    supportReaderScriptLoaded.value = true
}

/* ------------------------------------------------------------- */
/*                       HARDWARE EVENTS                         */
/* ------------------------------------------------------------- */
function goToSoftware () {
    currentStep.value = 2
    referenceSoftware.value.detectCard()
}

/* ------------------------------------------------------------- */
/*                       SOFTWARE EVENTS                         */
/* ------------------------------------------------------------- */
function goToCardOperations (params) {
    // When we read only we have only 3 steps (index 2)
    currentStep.value = isReadOnly.value ? 2 : 3

    if (params) {
        cardOperationsParams.value = params
        referenceCardOperations.value.activateChannel(params)
    } else {
        referenceCardOperations.value.activateChannel(cardOperationsParams.value)
    }
}

/* ------------------------------------------------------------- */
/*                   CARD OPERATIONS EVENTS                      */
/* ------------------------------------------------------------- */
function operationFinished (params) {
    emit('operationFinished', params)
}

/* ------------------------------------------------------------- */
/*                    SUB COMPONENTS EVENTS                      */
/* ------------------------------------------------------------- */
function downloadXSCP () {
    currentStep.value = 0
    referenceXscp.value.downloadXSCP()
}

function goToDrivers () {
    currentStep.value = 0
    referenceXscp.value.installDrivers()
}

function initConnection () {
    currentStep.value = 0
    referenceXscp.value.init()
}

function launchXSCP () {
    currentStep.value = 0
    referenceXscp.value.launchXSCP()
}

function seeCardContent () {
    currentStep.value = 3
    referenceCardOperations.value.seeCardContent()
}
</script>
